
<script setup lang="ts">
import BaseForm from "~/components/ui/BaseForm.vue";
import Rata from "~/components/inputs/Rata.vue";
import Perks from "~/components/static/Perks.vue";
import { getPromoModel, getModelPromoKO, isPromoCreditSelected } from "~/utils/promo";
import type { IRate } from "~/interfaces";

const locale = await getLocale("forms", "rate_personal_credit");
const promoLocale = await getLocale("forms", "rate_promo");


defineComponent({
  name: "RatePersonalCredit",
});

const timeOut = ref();

const { showFlashMessage } = defineProps({
  showFlashMessage: { type: Boolean, default: false },
});

onMounted(() => {
  updateStateValue("showFlashMessage", showFlashMessage);
  timeOut.value = setTimeout(() => {
    updateStateValue(
      "flashMessage",
      "Un utente ha appena inviato la sua richiesta di prestito. Continua anche tu, ti bastano solo 3 minuti!",
    );
  }, 5000);
});

onUnmounted(() => {
  clearTimeout(timeOut.value);
});

const route = useRoute();
const emit = defineEmits(["init", "submit", "upgrade"]);
const credit = computed(() => {
  return route.path !== RATE_UPGRADE_PATH
    ? getStateValue("selectedCredit")
    : getStateValue("limitedCredit");
});

const modelValue = computed(()=>{
  if (isPromoCreditSelected()) {
    return getPromoModel()!;
  }
  return getModel(credit.value);
});
const otherOffers = computed<IRate[]>(() => {
  const allOffers: IRate[] = personalCreditRates(credit.value, modelValue.value.rate) || [];
  return allOffers.filter((offer: IRate) => !offer.isUpgrade);
});
const selected = ref<IRate>(modelValue.value);

const isActive = (model: IRate) => {
  return (
    selected.value.imp_finanz === model.imp_finanz &&
    selected.value.rate === model.rate
  );
};
const toggleSelected = (rate: IRate) => {
  if (window.innerWidth < 1024) selected.value = rate;
};

const promoTerms = computed(() => {
  const promoKO = getModelPromoKO();
  if (!promoKO || !promoKO.taeg || !promoKO.secci_url) return "";
  return promoLocale.promo_terms
    .replace("%taeg_ko", promoKO.taeg)
    .replace("%secci_ko", promoKO.secci_url);
});

const submit = (model: IRate, rateType: string | null = null) => {
  updateStateValue("rateType", rateType);
  emit("submit", model);
};

const upgrade = (model: IRate, rateType: string | null = null) => {
  updateStateValue("rateType", rateType);
  emit("upgrade", model);
};
onMounted(() => emit("init"));



const iconsCreditNew = [
  {
    text: '<span class="font-semibold">Dati inseriti nella richiesta</span> <br />come reddito, spese, situazione lavorativa',
    icon: "Data",
  },
  {
    text: '<span class="font-semibold">Verifica situazione finanziaria</span> <br />Cofidis consulta i Sistemi di Informazione Creditizia (SIC)',
    icon: "Check",
  },
  {
    text: '<span class="font-semibold">Valutazione merito creditizio</span> <br />Analisi della tua capacità di rimborso e storico dei pagamenti',
    icon: "DataLayers",
  },
  {
    text: '<span class="font-semibold">Offerta su misura per te</span> <br />Importo, durata e tasso calibrati sul tuo profilo',
    icon: "Lightbulb",
  }
];
const iconComponents = computed(() => {
  return iconsCreditNew.map((icon) => iconComponent(icon));
});


</script>

<template>
  <BaseForm>
    <template #upper>
      <div class="offers my-4 sm:my-8">
        <div class="px-2 sm:px-6 lg:px-12">
          <h1 class="pageTitle">
            {{ locale.title }}
          </h1>
        </div>
        <div
          class="rate-offer-helper bg-yellow text-center lg:text-left py-4 mt-4"
        >
          <h3 class="text-center md:text-left md:text-xl font-semibold text-gray-700 leading-tight py-1 px-2 sm:px-6 lg:px-12">
            <span>{{ locale.offer_helper }}</span>
          </h3>
        </div>
        
        <div
          class="rates-container px-2 sm:px-6 lg:px-12 pt-6 pb-2"
        >
          <ul class="max-lg:mb-4" data-testid="rataOur">
            <Rata
              :model="modelValue"
              :active="isActive(modelValue)"
              type="our"
              @selected="toggleSelected"
              @submit="submit"
            />
          </ul>
          <p
            v-if="promoTerms"
            ref="terms"
            class="text-center sm:text-left text-11px sm:text-11.5px leading-tight mt-10 lg:mt-6 px-2 sm:px-0"
            v-html="promoTerms"
          />
          <h3
            class="text-base mb-4 lg:mb-6 mt-6 text-center lg:text-left"
          >
            {{ locale.other_offers }}
          </h3>
          <div
            v-if="otherOffers.length"
            class="rataOthers lg:mb-6"
            data-testid="rataOthers"
          >
            <Rata
              v-for="(model, index) in otherOffers"
              :key="index"
              class="mb-2"
              :model="model"
              :active="isActive(model)"
              type="other"
              :data-testid="`rataOther${model.rate}`"
              @selected="toggleSelected"
              @submit="submit(model, 'proposal_other')"
            />
          </div>
        </div>
      </div>
      <div class="spacer h-6" />
    </template>
    
  </BaseForm>

  <div class="mt-0 mb-12">
    <div class="flex flex-col items-center">      
      <h2 class="pageTitle" style="text-align: center;">
        La tua offerta è personalizzata
      </h2>
      <p class="pageSubtitle" style="text-align: center;">
        Ecco perchè può variare:
      </p>
    </div>

    

    <div class="grid grid-cols-1 md:grid-cols-2 md:gap-6 mb-6 px-8 md:px-20">
      <div class="md:inline-flex md:flex-col md:ml-auto contents md:contents-none">
        <div 
          v-for="(el, index) in iconComponents.slice(0, 2)" 
          :key="index" 
          class="flex items-center md:items-start"
          :class="{ 'mt-3': index > 0 }"
        >
          <div class="pr-3 flex-shrink-0">
            <component :is="el.icon"></component>
          </div>
          <div>
            <p v-html="el.text"></p>
          </div>
        </div>
      </div>

      <div class="contents md:contents-none md:flex md:flex-col">
        <div 
          v-for="(el, index) in iconComponents.slice(2, 4)" 
          :key="index + 2" 
          class="flex items-center md:items-start"
          :class="{ 'mt-3 md:mt-0': index + 2 > 0 }"
        >
          <div class="pr-3 flex-shrink-0">
            <component :is="el.icon"></component>
          </div>
          <div>
            <p v-html="el.text"></p>
          </div>
        </div>
      </div>
    </div>

    <div class="text-center text-sm mx-10">
      L'offerta definitiva e a te dedicata viene stabilita dopo un'analisi approfondita di tutti i dati indicati. 
    </div>

  </div>

  <Perks  />
  
</template>

<style scoped></style>
